import { usePollingInterval } from 'src/hooks/usePollingInterval'
import type { Theme } from '@material-ui/core'
import { Box, Divider, makeStyles } from '@material-ui/core'
import type { MessageActivity, MessagesQuery, UnreadMessagesQuery } from '@paintscout/api'
import {
  FullMessageActivityFragmentDoc,
  MessagesDocument,
  sendMessageOptimisticResponse,
  UnreadMessagesDocument,
  useDeleteMessageMutation,
  useMessagesQuery,
  useNewMessagesQuery,
  useReadMessagesMutation,
  useSendMessageMutation
} from '@paintscout/api'
import { QuoteContext, useQuote } from '@paintscout/react-quote'
import type { MessageInputProps } from '@paintscout/ui'
import {
  ActivityTimeline,
  Grid,
  MessageInput,
  Alert,
  NoResults,
  Spinner,
  Typography,
  useClientOptions,
  useUser,
  useInfiniteScroll
} from '@paintscout/ui'
import { createQuoteMessageActivity } from '@paintscout/util/activity'
import { getObjectLabels, getOwnerFromUser } from '@paintscout/util/builder'
import { removeTypenames } from '@paintscout/util/util'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import uniqBy from 'lodash/uniqBy'
import { useSnackbar } from 'notistack'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import BaseSidebarPage from '../BaseSidebarPage'
import * as Sentry from '@sentry/react'
import type { QuoteDocument } from 'paintscout'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative'
  },
  messageInput: {
    background: 'white',
    position: 'sticky',
    top: 0,
    zIndex: 5
  },
  spinner: {
    marginTop: theme.spacing()
  },
  timeline: {
    paddingTop: theme.spacing()
  },
  basePage: {
    paddingLeft: 0,
    paddingRight: 0
  }
}))

const ACTIVITY_LIMIT = 20

interface QuoteChatProps {
  editable?: boolean
  permissionToEdit?: boolean
  permissionToSendQuote?: boolean
  permissionToEditPresentationSection?: boolean
  onClick?: (event: React.MouseEvent) => any
}

function QuoteChat({ permissionToSendQuote }: QuoteChatProps) {
  const classes = useStyles({})

  const { options } = useClientOptions()
  const { quote } = useContext(QuoteContext)
  const objectLabels = getObjectLabels({ options })
  const ref = useRef(null)
  const { enqueueSnackbar } = useSnackbar()
  const [pollTimestamp, setPollTimestamp] = useState(Date.now())

  const { loading, data, error, fetchMore, updateQuery } = useMessagesQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    pollInterval: usePollingInterval(60000),
    variables: {
      quoteId: quote._id,
      limit: ACTIVITY_LIMIT
    },
    onError: useCallback((error) => {
      Sentry.captureException(error, { tags: { quoteId: quote._id } })
    }, [])
  })

  const [readMessages] = useReadMessagesMutation({
    update: (cache, result) => {
      if (result?.data?.readMessages?.length > 0) {
        cache.writeQuery<UnreadMessagesQuery>({
          query: UnreadMessagesDocument,
          variables: {
            quoteId: quote._id,
            limit: 20
          },
          data: {
            __typename: 'Query',
            unreadMessages: []
          }
        })
      }
    }
  })

  const [deleteMessage] = useDeleteMessageMutation({
    onError: useCallback((error) => {
      Sentry.captureException(error, { tags: { quoteId: quote._id } })
      enqueueSnackbar('Unable to delete message', { variant: 'error' })
    }, [])
  })

  useEffect(() => {
    if (
      data?.messages?.messages?.some(
        (msg) =>
          msg.__typename === 'MessageActivity' &&
          msg.details?.message?.from?.isCustomer &&
          !msg.details?.message?.readAt
      )
    ) {
      // timeout is slightly hacky, but keeps sidebar animation smooth
      const timeout = setTimeout(() => {
        readMessages({ variables: { quoteId: quote._id } })
      }, 500)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [data])

  const totalRows = data?.messages?.total_rows
  const messages = (data && data.messages && data.messages.messages) || []
  const hasMore = !!data?.messages?.bookmark && totalRows > messages.length

  useNewMessagesQuery({
    pollInterval: usePollingInterval(60000),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: loading,
    variables: useMemo(() => {
      return {
        quoteId: quote._id,
        since: pollTimestamp
      }
    }, [pollTimestamp]),
    onCompleted: useCallback(
      (data) => {
        if (data?.newMessages?.length > 0) {
          setPollTimestamp(Date.now())
          updateQuery((prev) => {
            const newMessages = data.newMessages.filter(
              (message) => !prev.messages.messages.find(({ _id }) => _id === message._id)
            )
            const updatedMessages = data.newMessages.filter(
              (message) => !!prev.messages.messages.find(({ _id }) => _id === message._id)
            )

            return {
              ...prev,
              messages: {
                ...prev.messages,
                total_rows: prev.messages.total_rows + data.newMessages.length,
                messages: [
                  ...newMessages,
                  ...prev.messages.messages.map((message) => {
                    const updatedMessage = updatedMessages.find(({ _id }) => _id === message._id)
                    return updatedMessage || message
                  })
                ]
              }
            }
          })
        }
      },
      [updateQuery]
    )
  })

  useInfiniteScroll({
    ref,
    disabled: !hasMore || loading,
    scrollPercentage: 75,
    onLoadMore: () => {
      fetchMore({
        variables: {
          quoteId: quote._id,
          bookmark: data.messages.bookmark,
          limit: ACTIVITY_LIMIT
        },
        updateQuery(prev, { fetchMoreResult }) {
          if (!prev) {
            return fetchMoreResult
          }

          if (!fetchMoreResult.messages) {
            return prev
          }
          return {
            ...prev,
            ...fetchMoreResult,
            messages: {
              ...prev.messages,
              ...fetchMoreResult.messages,

              // duplicates will come in if newMessages has returned any new docs, so we need to filter them out
              messages: uniqBy([...prev.messages.messages, ...fetchMoreResult.messages.messages], '_id')
            }
          }
        }
      })
    }
  })

  function handleAction(action: string, message: MessageActivity) {
    switch (action) {
      case 'delete':
        deleteMessage({
          variables: {
            id: message._id
          },
          optimisticResponse: {
            deleteMessage: true
          },
          update: (cache) => {
            const id = defaultDataIdFromObject({ ...message, __typename: 'MessageActivity' })
            const fragment = cache.readFragment<MessageActivity>({
              id,
              fragmentName: 'FullMessageActivity',
              fragment: FullMessageActivityFragmentDoc
            })

            cache.writeFragment({
              id,
              fragmentName: 'FullMessageActivity',
              fragment: FullMessageActivityFragmentDoc,
              data: {
                ...fragment,
                details: {
                  ...fragment.details,
                  message: {
                    ...fragment.details?.message,
                    deleted: true
                  }
                }
              }
            })
          }
        })

        break
    }
  }

  const isEmpty = !loading && messages.length === 0

  const quoteHasBeenSent =
    (Object.keys(quote.urls ?? {}).length > 0 || !!quote.dates?.client?.sent) && quote.status.value !== 'draft'
  let warning
  if (!quote.owner) {
    warning = `Chat will be available after this ${objectLabels.quote.value} has been assigned to an Estimator.`
  } else if (!quote.contact) {
    warning = `Chat will be available after this ${objectLabels.quote.value} has a contact.`
  } else if (!quote?._rev) {
    warning = `Chat will be available after this ${objectLabels.quote.value} has been saved.`
  } else if (!quoteHasBeenSent) {
    warning = `Contact will not be notified until ${objectLabels.quote.value} has been sent.`
  }

  return (
    <div ref={ref} className={classes.root}>
      <div className={classes.messageInput}>
        {permissionToSendQuote && (
          <MessageInputForUser
            messages={messages as MessageActivity[]}
            disabled={!quote.contact || !quote.owner || !quote?._rev}
          />
        )}
        <Divider />
      </div>
      <BaseSidebarPage className={classes.basePage}>
        {error && (
          <Box marginTop={4}>
            <NoResults message={'There was a problem loading messages'} />
          </Box>
        )}
        {warning && (
          <Box marginTop={4}>
            <Alert severity={'info'} content={warning} />
          </Box>
        )}

        {isEmpty && !error && <EmptyMessage />}
        <Grid container spacing={0} className={classes.timeline}>
          <Grid item xs={12}>
            <ActivityTimeline
              isQuote={true}
              activities={messages}
              loading={loading && messages.length === 0}
              shimRowAmount={10}
              showDocTypeDetails={false}
              showContent
              onAction={handleAction}
            />
            {hasMore && loading && <Spinner className={classes.spinner} />}
          </Grid>
        </Grid>
      </BaseSidebarPage>
    </div>
  )
}

const EmptyMessage = () => {
  return (
    <Box marginTop={4} padding={1}>
      <Typography variant="subtitle1" align="center">
        No messages
      </Typography>
    </Box>
  )
}

function MessageInputForUser({ messages, ...props }: { messages: MessageActivity[] } & MessageInputProps) {
  const [value, setValue] = useState('')
  const { quote, updateQuote } = useQuote()
  const { user } = useUser()
  const { enqueueSnackbar } = useSnackbar()

  const lastMessage = useMemo(
    () => messages.sort((a, b) => b?.details?.timestamp - a?.details?.timestamp)[0],
    [messages]
  )

  const [sendMessage] = useSendMessageMutation({
    update: (cache, { data: { sendMessage } }) => {
      const { activity, quote } = sendMessage
      const cacheData = cache.readQuery<MessagesQuery>({
        query: MessagesDocument,
        variables: {
          quoteId: quote._id,
          limit: ACTIVITY_LIMIT
        }
      })

      cache.writeQuery<MessagesQuery>({
        query: MessagesDocument,
        variables: {
          quoteId: quote._id,
          limit: ACTIVITY_LIMIT
        },
        data: {
          ...cacheData,
          messages: {
            ...cacheData.messages,
            total_rows: cacheData.messages.total_rows + 1,
            // sometimes sendMessage mutation response happens after the "new message" listener picks it up,
            // so we need to uniq it
            messages: uniqBy([activity, ...cacheData.messages.messages], '_id')
          }
        }
      })
    }
  })

  async function handleSend() {
    const message = value
    setValue('')

    const timestamp = Date.now()
    try {
      // send notification if the last message was longer than 5 minutes ago, or if last message was from customer
      const sendNotification =
        !!lastMessage?.details?.message?.from?.isCustomer ||
        (lastMessage?.details?.timestamp ?? 0) < Date.now() - 1000 * 60 * 5

      const { data } = await sendMessage({
        context: {
          onlineOnly: true
        },
        optimisticResponse: {
          sendMessage: sendMessageOptimisticResponse(
            createQuoteMessageActivity({
              quote,
              from: {
                userId: user.user_id,
                name: user.name,
                email: user.email,
                isCustomer: false
              },
              message,
              owner: getOwnerFromUser(user),
              timestamp
            }),
            quote
          )
        },
        variables: {
          message,
          quoteId: quote._id,
          timestamp,
          sendNotification
        }
      })
      const updatedQuote = (data?.sendMessage?.quote as any as QuoteDocument) ?? null
      if (updatedQuote) {
        updateQuote({
          quote: removeTypenames(updatedQuote),
          blockDirty: true
        })
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { quoteId: quote._id } })
      setValue(message)

      enqueueSnackbar('Unable to send message', { variant: 'error' })
    }
  }

  return (
    <MessageInput
      inputProps={{
        'data-testid': 'chat-input'
      }}
      value={value}
      onChange={(ev) => setValue(ev.currentTarget.value)}
      onSend={handleSend}
      {...props}
    />
  )
}

export default QuoteChat
